import * as THREE from "three";
import type { Spinner } from "./spinTable";

/** What the rig is playing, as the driver needs it: the loop (its clip time runs at `timeScale`) and the one-shot. */
export interface SpinPlay {
  readonly loop: THREE.AnimationAction | null;
  readonly loopName: string;
  /** The loop is posed by phase (AnimRig.seekLoop), not run by the mixer: its phase this frame, 0–1. */
  readonly seekPhase: number | null;
  readonly oneShot: THREE.AnimationAction | null;
}

/**
 * Turns a rig's spinners (spinTable.ts) by code, after the mixer, as `base · turn(axis, angle) · shot · held · wobble`:
 * - angle: only ever accumulates — the playing loop's rate × its clip time;
 * - shot: the playing one-shot's rotation as a change from where it was when the one-shot was first seen; when it
 *   ends (or is cut) its turn about the axis is added to the angle and any tilt it left is `held`, which settles back
 *   to nothing over HeldSeconds;
 * - wobble: the loop's tilt of the axis at its clip time (it closes over the loop), cross-faded on a change of loop.
 * A loop wrapping, a one-shot starting or ending, a cross-fade or a change of loop never moves the part.
 */
export class SpinDriver {
  /** The time a one-shot's leftover tilt takes to settle (s, a time constant). */
  static readonly HeldSeconds = 0.4;
  /** The cross-fade of the wobble from one loop clip to the next (s). */
  static readonly WobbleFadeSeconds = 0.25;

  private readonly nodes: (THREE.Object3D | null)[];
  private readonly angle: Float64Array;
  private readonly from: THREE.Quaternion[];
  private readonly shotQ: THREE.Quaternion[];
  private readonly keyed: boolean[];
  private readonly held: THREE.Quaternion[];
  private readonly wobble: THREE.Quaternion[];
  private readonly wobbleFrom: THREE.Quaternion[];
  private shot: THREE.AnimationAction | null = null;
  private shotTime = 0;
  private loop: THREE.AnimationAction | null = null;
  private lastPhase = 0;
  private wobbleAt = Infinity;
  private started = false;

  constructor(root: THREE.Object3D, private readonly spinners: readonly Spinner[]) {
    const quats = (): THREE.Quaternion[] => spinners.map(() => new THREE.Quaternion());
    this.nodes = spinners.map((s) => THREE.PropertyBinding.findNode(root, s.node) as THREE.Object3D | null);
    this.angle = new Float64Array(spinners.length);
    this.from = quats(); this.shotQ = quats(); this.held = quats(); this.wobble = quats(); this.wobbleFrom = quats();
    this.keyed = spinners.map(() => false);
  }

  /** Advances by `dt` seconds of rig time and poses the spinners (after the mixer). */
  step(dt: number, play: SpinPlay): void {
    this.followLoop(dt, play);
    this.followShot(play.oneShot);
    const settle = dt > 0 ? 1 - Math.exp(-dt / SpinDriver.HeldSeconds) : 0;
    for (let i = 0; i < this.spinners.length; i++) {
      const n = this.nodes[i];
      if (!n) continue;
      const s = this.spinners[i];
      this.held[i].slerp(IDENTITY, settle);
      n.quaternion.setFromAxisAngle(s.axis, this.angle[i]).premultiply(s.base);
      if (this.keyed[i]) n.quaternion.multiply(_q.copy(this.from[i]).invert().multiply(this.shotQ[i]));
      n.quaternion.multiply(this.held[i]).multiply(this.wobble[i]);
    }
  }

  /** Back to the first pose (a pooled view reused). */
  reset(): void {
    this.angle.fill(0);
    for (const q of [...this.held, ...this.wobble]) q.identity();
    this.keyed.fill(false);
    this.shot = null;
    this.loop = null;
    this.wobbleAt = Infinity;
    this.started = false;
  }

  private followLoop(dt: number, play: SpinPlay): void {
    const a = play.loop;
    if (!a) { this.loop = null; return; }
    const clip = a.getClip();
    if (a !== this.loop) {
      // the first loop starts where its clip starts (a random start included); a later one carries on from here
      if (!this.started) this.addLoop(play.loopName, a.time);
      this.loop = a;
      this.lastPhase = play.seekPhase ?? 0;
      this.wobbleAt = this.started ? 0 : Infinity;
      for (let i = 0; i < this.spinners.length; i++) this.wobbleFrom[i].copy(this.wobble[i]);
      this.started = true;
    } else if (play.seekPhase !== null) {
      const d = play.seekPhase - this.lastPhase;
      this.lastPhase = play.seekPhase;
      this.addLoop(play.loopName, (d - Math.round(d)) * clip.duration);
    } else this.addLoop(play.loopName, dt * a.timeScale);
    this.wobbleAt += dt;
    const fade = Math.min(1, this.wobbleAt / SpinDriver.WobbleFadeSeconds);
    for (let i = 0; i < this.spinners.length; i++) {
      const sw = this.spinners[i].loops.get(play.loopName)?.swing;
      if (sw) _q.fromArray(sw.evaluate(a.time)); else _q.identity();
      this.wobble[i].copy(this.wobbleFrom[i]).slerp(_q, fade);
    }
  }

  private addLoop(name: string, clipSeconds: number): void {
    for (let i = 0; i < this.spinners.length; i++) this.angle[i] += (this.spinners[i].loops.get(name)?.rate ?? 0) * clipSeconds;
  }

  private followShot(a: THREE.AnimationAction | null): void {
    // a one-shot played again while it plays is the same action, reset: a new one-shot all the same
    const again = a !== null && a === this.shot && a.time < this.shotTime;
    this.shotTime = a ? a.time : 0;
    if (a !== this.shot || again) {
      // the one that ended (or was cut) keeps the turn it made; a tilt it left settles away (`held`)
      for (let i = 0; i < this.spinners.length; i++) if (this.keyed[i]) this.fold(i);
      this.keyed.fill(false);
      this.shot = a;
      if (!a) return;
      this.started = true;
    }
    if (!a) return;
    const name = a.getClip().name;
    for (let i = 0; i < this.spinners.length; i++) {
      const it = this.spinners[i].shots.get(name);
      if (!it) continue;
      this.shotQ[i].fromArray(it.evaluate(a.time));
      if (!this.keyed[i]) { this.from[i].copy(this.shotQ[i]); this.keyed[i] = true; }
    }
  }

  /** Splits a finished one-shot's change into a turn about the axis (onto the angle) and the tilt left (held). */
  private fold(i: number): void {
    const s = this.spinners[i], ax = s.axis;
    const d = _q.copy(this.from[i]).invert().multiply(this.shotQ[i]);
    const p = d.x * ax.x + d.y * ax.y + d.z * ax.z;
    this.angle[i] += 2 * Math.atan2(p, d.w);
    _t.set(ax.x * p, ax.y * p, ax.z * p, d.w).normalize().invert();
    this.held[i].premultiply(_t.multiply(d));
  }
}

const IDENTITY = new THREE.Quaternion();
const _q = new THREE.Quaternion(), _t = new THREE.Quaternion();
